// 🔎 TEMP DEBUG — REMOVE AFTER
window.__LOG_FLEX_CART__ = async function () {
  const cart = await fetch("/cart.js").then((r) => r.json());
  console.log("🧾 CART ITEMS:", cart.items);
  return cart.items;
};

/* flex-auto-fee.js (OPTIMIZED — FASTER SYNC + PARALLEL PREFETCH + REDUCED DEBOUNCE)
   Reconcile Flex plan quantity to match phone quantity.
   - Cart-only guard (won't run on search/predictive pages)
   - Chain-safe fetch hook (won't stomp other app embeds like Findter)
   - Optional XHR hook
   - Checkout validation: fetches live cart at click time, forces sync if qty mismatch, then proceeds
   - Variant ID prefetched on install — eliminates one round-trip during sync
   - Debounce reduced: 80ms (was 200ms)
   - notifyCartUI no longer re-fetches — reuses the cart already in memory
   - Retry backoff tightened

   PHONE detection (cart.js-safe):
   - properties._flex_role === "phone"  (best / future-proof)
   - OR product_type === "phones"       (Shopify admin product type)
   - OR tags includes "phones"          (Shopify tags, comma-separated string)
   - OR properties._has_plan == 1       (optional)
   - OR fallback heuristic on title (since cart.js doesn't include product options)
*/

(() => {
  // ---- RUN ONLY WHERE IT MATTERS (CART CONTEXT GUARD) ----
  const isCartContext =
    document.querySelector("cart-drawer") ||
    document.querySelector("cart-items") ||
    document.querySelector("form[action='/cart']") ||
    window.location.pathname.includes("/cart");

  if (!isCartContext) return;

  // Prevent double-install
  if (window.__FLEX_CART_SYNC_INSTALLED__) return;

  // ---- CONFIG ----
  const PLAN_HANDLE_FALLBACK = "ting-sim-card-add";
  const PLAN_TITLE_FALLBACK = "Ting SIM Card";

  const DEFAULT_FLEX_ERROR = "Could not add Flex plan automatically. Please try again.";
  const FRIENDLY_FLEX_ERROR =
    "Flex Mobile plan limit reached. We couldn't add another Flex Mobile plan because there isn't enough plan inventory available right now. " +
    "Please remove a device or continue with the plans already in your cart.";

  // ---- STATE / LOCKS ----
  let debounceTimer = null;
  let running = false;
  let rerunRequested = false;

  // ✅ PREFETCH: Kick off variant ID lookup immediately on install.
  // By the time a user adds a phone and triggers sync, this will already be resolved.
  let variantIdPromise = null;
  function ensureVariantPrefetch() {
    if (!variantIdPromise) {
      variantIdPromise = getPlanVariantIdFromHandle(PLAN_HANDLE_FALLBACK).catch(() => null);
    }
    return variantIdPromise;
  }

  // ---- HELPERS ----
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  async function fetchCart() {
    const r = await fetch("/cart.js", { credentials: "same-origin" });
    if (!r.ok) throw new Error(`cart.js ${r.status}`);
    return r.json();
  }

  async function postJSON(url, body) {
    const r = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (r.status === 429) {
      const err = new Error("429 rate limited");
      err.status = 429;
      throw err;
    }

    if (!r.ok) {
      let details = null;
      let rawText = "";
      try {
        const ct = r.headers.get("content-type") || "";
        if (ct.includes("application/json")) {
          details = await r.json().catch(() => null);
        } else {
          rawText = await r.text().catch(() => "");
        }
      } catch (_) {}

      if (!details && !rawText) rawText = await r.text().catch(() => "");

      const err = new Error(`${url} ${r.status}`.slice(0, 200));
      err.status = r.status;
      err.details = details;
      err.rawText = rawText;
      throw err;
    }

    return r.json().catch(() => ({}));
  }

  async function getPlanVariantIdFromHandle(handle) {
    const key = `__flex_plan_variant_${handle}`;
    const cached = sessionStorage.getItem(key);
    if (cached) return Number(cached);

    const r = await fetch(`/products/${handle}.js`, { credentials: "same-origin" });
    if (!r.ok) throw new Error(`products/${handle}.js ${r.status}`);
    const p = await r.json();
    const id = p?.variants?.[0]?.id;
    if (!id) throw new Error(`No variant for ${handle}`);
    sessionStorage.setItem(key, String(id));
    return Number(id);
  }

  // ✅ CART-SAFE PHONE DETECTION
  function isPhoneLineItem(i) {
    if (!i) return false;

    if (i?.properties?._flex_role === "phone") return true;

    if ((i?.product_type || "").toLowerCase() === "phones") return true;

    const tagsRaw = String(i?.tags || "");
    if (tagsRaw.split(",").map((t) => t.trim().toLowerCase()).includes("phones")) return true;

    const hasPlanFlag = String(i?.properties?._has_plan || "").trim();
    if (hasPlanFlag === "1" || hasPlanFlag.toLowerCase() === "true") return true;

    const title = String(i.title || "");
    if (title.includes(" / ")) {
      const parts = title.split(" / ");
      if (parts.length >= 3) return true;
      if (/\b(no plan|plan)\b/i.test(title)) return true;
    }

    return false;
  }

  function summarize(cart) {
    const items = cart.items || [];

    const phones = items.filter(isPhoneLineItem);
    const phoneQty = phones.reduce((sum, i) => sum + (i.quantity || 0), 0);

    let service = items.find((i) => i?.properties?._flex_role === "service");
    if (!service) service = items.find((i) => (i.title || "").includes(PLAN_TITLE_FALLBACK));

    const serviceQty = service ? service.quantity || 0 : 0;
    return { phoneQty, service, serviceQty };
  }

  // ---- MESSAGE REPLACEMENT ----
  function replaceDefaultFlexErrorText() {
    try {
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      let node;
      let replaced = false;

      while ((node = walker.nextNode())) {
        const val = node.nodeValue || "";
        if (val.includes(DEFAULT_FLEX_ERROR)) {
          node.nodeValue = val.replace(DEFAULT_FLEX_ERROR, FRIENDLY_FLEX_ERROR);
          replaced = true;
        }
      }

      const candidates = [
        ".cart-notification__error",
        ".cart__warnings",
        ".cart-errors",
        "[role='alert']",
        "[aria-live='polite']",
        "[aria-live='assertive']",
      ];

      candidates.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          if (el && typeof el.textContent === "string" && el.textContent.includes(DEFAULT_FLEX_ERROR)) {
            el.textContent = el.textContent.replace(DEFAULT_FLEX_ERROR, FRIENDLY_FLEX_ERROR);
            replaced = true;
          }
        });
      });

      return replaced;
    } catch (_) {
      return false;
    }
  }

  // ---- FRIENDLY BANNER ----
  const FLEX_MSG_ID = "flex-plan-limit-message";
  let lastFlexMsgAt = 0;

  function showFlexPlanLimitBanner() {
    const now = Date.now();
    if (now - lastFlexMsgAt < 2500) return;
    lastFlexMsgAt = now;

    const host =
      document.querySelector("cart-drawer") ||
      document.querySelector("#CartDrawer") ||
      document.querySelector(".cart-drawer") ||
      document.querySelector("main") ||
      document.body;

    if (!host) return;

    const existing = document.getElementById(FLEX_MSG_ID);
    if (existing) existing.remove();

    const wrap = document.createElement("div");
    wrap.id = FLEX_MSG_ID;
    wrap.setAttribute("role", "status");
    wrap.style.cssText = `
      position: relative;
      margin: 12px auto;
      max-width: 900px;
      padding: 12px 14px;
      border: 1px solid rgba(0,0,0,.12);
      border-radius: 10px;
      background: #fff7e6;
      color: #3a2a00;
      font-size: 14px;
      line-height: 1.35;
      z-index: 9999;
    `;

    const title = document.createElement("div");
    title.style.cssText = "font-weight:700; margin-bottom:6px;";
    title.textContent = "Sim Card not added";

    const body = document.createElement("div");
    body.textContent = FRIENDLY_FLEX_ERROR;

    const close = document.createElement("button");
    close.type = "button";
    close.setAttribute("aria-label", "Dismiss");
    close.textContent = "×";
    close.style.cssText = `
      position:absolute; top:6px; right:10px;
      border:0; background:transparent; cursor:pointer;
      font-size:22px; line-height:1; color: inherit;
      padding: 2px 6px;
    `;
    close.addEventListener("click", () => wrap.remove());

    wrap.appendChild(title);
    wrap.appendChild(body);
    wrap.appendChild(close);

    if (host.matches("cart-drawer") || host.id === "CartDrawer" || host.classList.contains("cart-drawer")) {
      const drawerContent =
        host.querySelector(".drawer__inner") ||
        host.querySelector(".drawer__contents") ||
        host;
      drawerContent.prepend(wrap);
    } else {
      host.prepend(wrap);
    }

    setTimeout(() => {
      const el = document.getElementById(FLEX_MSG_ID);
      if (el) el.remove();
    }, 10000);
  }

  function isLikelyPlanInventoryLimitError(err) {
    const status = err?.status;
    const raw = String(err?.rawText || err?.message || "").toLowerCase();
    const msg = String(err?.details?.message || "").toLowerCase();
    const desc = String(err?.details?.description || "").toLowerCase();
    const hay = `${raw} ${msg} ${desc}`;

    const looksLikeInventory =
      hay.includes("sold out") ||
      hay.includes("not enough") ||
      hay.includes("inventory") ||
      hay.includes("unavailable") ||
      hay.includes("cannot be added") ||
      hay.includes("can't add") ||
      hay.includes("cannot add");

    return status === 422 && looksLikeInventory;
  }

  // ---- UI REFRESH ----
  function tryPublishCartUpdate(cart) {
    try {
      if (typeof window.publish !== "function") return false;
      ["cart-update", "cartUpdate", "cart:update", "cart:updated", "cart:refresh", "theme:cart:update"].forEach((evt) => {
        try {
          window.publish(evt, { source: "flex-sync", cartData: cart });
        } catch (_) {}
      });
      return true;
    } catch (_) {
      return false;
    }
  }

  // ✅ Dawn-native refresh (does NOT nuke checkout button)
  async function refreshDawnCartUI(cart) {
    try {
      // Cart page
      const cartItems = document.querySelector("cart-items");
      if (cartItems && typeof cartItems.onCartUpdate === "function") {
        cartItems.onCartUpdate();
      }

      // Cart drawer
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer && typeof cartDrawer.renderContents === "function" && cart) {
        cartDrawer.renderContents(cart);
      }

      // Cart notification
      const cartNotif = document.querySelector("cart-notification");
      if (cartNotif && typeof cartNotif.renderContents === "function" && cart) {
        cartNotif.renderContents(cart);
      }
    } catch (_) {}
  }

  // ✅ OPTIMIZED: accepts the cart already fetched — no extra round-trip
  async function notifyCartUI(cart) {
    if (!cart) {
      cart = await fetchCart().catch(() => null);
    }
    if (!cart) return;

    tryPublishCartUpdate(cart);
    await refreshDawnCartUI(cart);
    replaceDefaultFlexErrorText();

    try {
      document.dispatchEvent(new CustomEvent("cart:updated", { detail: cart }));
      document.dispatchEvent(new CustomEvent("cart:refresh", { detail: cart }));
    } catch (_) {}
  }

  // ---- CORE SYNC ----
  async function reconcileOnce() {
    // ✅ Kick off variant prefetch and cart fetch IN PARALLEL
    const [cart] = await Promise.all([
      fetchCart(),
      ensureVariantPrefetch(), // warms the cache, result used below
    ]);

    const { phoneQty, service, serviceQty } = summarize(cart);

    if (phoneQty === serviceQty) return;

    const maxAttempts = 5;
    let attempt = 0;

    while (attempt < maxAttempts) {
      attempt++;

      try {
        // ---- Reduce sim qty ----
        if (phoneQty < serviceQty && service) {
          const updatedCart = await postJSON("/cart/change.js", { id: service.key, quantity: phoneQty });
          await notifyCartUI(updatedCart);
          return;
        }

        // ---- Increase / add sim qty ----
        if (phoneQty > serviceQty) {
          const needed = phoneQty;

          if (service) {
            const updatedCart = await postJSON("/cart/change.js", { id: service.key, quantity: needed });
            await notifyCartUI(updatedCart);
            return;
          }

          // ✅ Variant ID already prefetched — no extra fetch here
          const variantId = await ensureVariantPrefetch();
          if (!variantId) throw new Error("Could not resolve plan variant ID");

          let updatedCart;
          try {
            updatedCart = await postJSON("/cart/add.js", {
              items: [{ id: variantId, quantity: needed, properties: { _flex_role: "service" } }],
            });
          } catch (e) {
            if (isLikelyPlanInventoryLimitError(e)) {
              replaceDefaultFlexErrorText();
              showFlexPlanLimitBanner();
              return;
            }
            throw e;
          }

          await notifyCartUI(updatedCart);
          return;
        }

        return;
      } catch (e) {
        if (isLikelyPlanInventoryLimitError(e)) {
          replaceDefaultFlexErrorText();
          showFlexPlanLimitBanner();
          return;
        }

        const status = e?.status;
        // ✅ Tighter retry backoff (was 350/500/800 × attempt)
        const delay =
          status === 429 ? 400 * attempt :
          status === 422 ? 250 * attempt :
          150 * attempt;

        if (attempt >= maxAttempts) throw e;
        await sleep(delay);

        // Re-fetch cart on retry so we have fresh state
        const freshCart = await fetchCart().catch(() => null);
        if (freshCart) {
          const re = summarize(freshCart);
          if (re.phoneQty === re.serviceQty) return; // already in sync, bail early
        }
      }
    }
  }

  async function runSync() {
    if (running) {
      rerunRequested = true;
      return;
    }

    running = true;
    window.__FLEX_SYNC_RUNNING__ = true;

    try {
      await reconcileOnce();
    } catch (e) {
      console.warn("[FLEX] sync failed:", e);
      replaceDefaultFlexErrorText();
      if (isLikelyPlanInventoryLimitError(e)) showFlexPlanLimitBanner();
    } finally {
      running = false;
      window.__FLEX_SYNC_RUNNING__ = false;

      if (rerunRequested) {
        rerunRequested = false;
        setTimeout(runSync, 100); // was 150ms
      }
    }
  }

  // ✅ REDUCED DEBOUNCE: 80ms instead of 200ms
  function scheduleSync() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(runSync, 80);
  }

  // ---- CHECKOUT VALIDATION ----
  function interceptCheckout() {
    function setBtnState(btn, text, disabled) {
      btn.disabled = disabled;
      if (btn.tagName === "INPUT") {
        btn.value = text;
      } else {
        btn.textContent = text;
      }
    }

    async function validateAndCheckout(e) {
      e.preventDefault();
      e.stopImmediatePropagation();

      const btn = e.currentTarget;

      if (btn.dataset.flexValidating === "1") return;
      btn.dataset.flexValidating = "1";

      const originalText = btn.tagName === "INPUT" ? btn.value : btn.textContent;
      const originalDisabled = btn.disabled;

      setBtnState(btn, "Checking cart…", true);

      try {
        const cart = await fetchCart();
        const { phoneQty, serviceQty } = summarize(cart);

        console.log(`[FLEX] checkout check — phones: ${phoneQty}, sims: ${serviceQty}`);

        if (phoneQty !== serviceQty) {
          setBtnState(btn, "Syncing cart…", true);
          clearTimeout(debounceTimer);
          await runSync();
          await sleep(200); // tightened from 300ms
        }

        setBtnState(btn, originalText, originalDisabled);
        btn.dataset.flexValidating = "0";

        if (btn.tagName === "A" && btn.href) {
          window.location.href = btn.href;
        } else {
          const form = btn.closest("form");
          if (form) {
            btn.removeEventListener("click", validateAndCheckout, true);
            btn.click();
            setTimeout(() => btn.addEventListener("click", validateAndCheckout, true), 200);
          } else {
            window.location.href = "/checkout";
          }
        }
      } catch (err) {
        console.warn("[FLEX] checkout validation error:", err);
        setBtnState(btn, originalText, originalDisabled);
        btn.dataset.flexValidating = "0";

        const form = btn.closest("form");
        if (form) {
          btn.removeEventListener("click", validateAndCheckout, true);
          btn.click();
          setTimeout(() => btn.addEventListener("click", validateAndCheckout, true), 200);
        } else {
          window.location.href = "/checkout";
        }
      }
    }

    function attachToCheckoutButtons() {
      const selectors = [
        'button[name="checkout"]',
        'button[type="submit"][name="checkout"]',
        'input[type="submit"][name="checkout"]',
        'a[href="/checkout"]',
        ".cart__checkout-button",
        "#checkout",
      ];

      selectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((btn) => {
          if (btn.dataset.flexCheckoutBound) return;
          btn.dataset.flexCheckoutBound = "1";
          btn.addEventListener("click", validateAndCheckout, true);
        });
      });
    }

    attachToCheckoutButtons();

    document.addEventListener("cart:updated", () => setTimeout(attachToCheckoutButtons, 300));
    document.addEventListener("cart:refresh", () => setTimeout(attachToCheckoutButtons, 300));
  }

  // ---- EXPOSE / MARK ----
  window.FLEX_SYNC = async () => runSync();
  window.__FLEX_CART_SYNC_INSTALLED__ = true;

  // ---- TRIGGERS ----
  document.addEventListener("DOMContentLoaded", () => {
    ensureVariantPrefetch(); // ✅ Kick off variant ID fetch immediately on page load
    scheduleSync();
    replaceDefaultFlexErrorText();
    interceptCheckout();
  });

  // ✅ CHAIN-SAFE FETCH HOOK
  if (!window.__FLEX_FETCH_HOOK_INSTALLED__) {
    window.__FLEX_FETCH_HOOK_INSTALLED__ = true;
    const prevFetch = window.fetch.bind(window);

    window.fetch = async (...args) => {
      const res = await prevFetch(...args);

      try {
        const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
        if (/\/cart\/(add|change|update|clear)\.js|\/cart\.js/.test(url)) {
          scheduleSync();
          setTimeout(replaceDefaultFlexErrorText, 50);
          setTimeout(replaceDefaultFlexErrorText, 250);
        }
      } catch (_) {}

      return res;
    };
  }

  // ✅ OPTIONAL: XHR HOOK
  if (!window.__FLEX_XHR_HOOK_INSTALLED__) {
    window.__FLEX_XHR_HOOK_INSTALLED__ = true;

    const origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.addEventListener("load", () => {
        try {
          if (typeof url === "string" && /\/cart\/(add|change|update|clear)\.js|\/cart\.js/.test(url)) {
            scheduleSync();
            setTimeout(replaceDefaultFlexErrorText, 50);
          }
        } catch (_) {}
      });
      return origOpen.apply(this, arguments);
    };
  }

  document.addEventListener(
    "click",
    (e) => {
      const el = e.target?.closest?.("button, a, input");
      if (!el) return;

      const looksLikeCartControl =
        el.name === "plus" ||
        el.name === "minus" ||
        el.classList?.contains("cart-remove-button") ||
        el.classList?.contains("quantity__button") ||
        el.getAttribute?.("href")?.includes("/cart/change") ||
        el.getAttribute?.("data-cart-remove") != null;

      if (looksLikeCartControl) scheduleSync();
    },
    true
  );
})();
